import type {
  CreateRoomDisplayPosterDTO,
  CreateRoomDisplayScreenDTO,
  PublicDisplayPayload,
  RoomDisplayPosterDTO,
  RoomDisplayScreenDetailDTO,
  RoomDisplayScreenListItemDTO,
  UpdateRoomDisplayPosterDTO,
  UpdateRoomDisplayScreenDTO,
} from "@/modules/room-displays/domain/contracts/room-displays";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type RoomDisplayVideoUploadInput = {
  filename: string;
  contentType: string;
  sizeBytes: number;
  videoDurationSec: number;
};

export const roomDisplaysApi = {
  list: (): Promise<{ data: RoomDisplayScreenListItemDTO[] }> =>
    fetcher.get(`${config.apiUrl}/room-displays`),

  getById: (id: string): Promise<RoomDisplayScreenDetailDTO> =>
    fetcher.get(`${config.apiUrl}/room-displays/${id}`),

  create: (
    input: CreateRoomDisplayScreenDTO,
  ): Promise<RoomDisplayScreenDetailDTO> =>
    fetcher.post(`${config.apiUrl}/room-displays`, input),

  update: (
    id: string,
    input: UpdateRoomDisplayScreenDTO,
  ): Promise<RoomDisplayScreenDetailDTO> =>
    fetcher.put(`${config.apiUrl}/room-displays/${id}`, input),

  remove: (id: string): Promise<{ ok: boolean }> =>
    fetcher.delete(`${config.apiUrl}/room-displays/${id}`),

  presignVideoUpload: (
    screenId: string,
    input: RoomDisplayVideoUploadInput,
  ): Promise<{ uploadUrl: string; key: string }> =>
    fetcher.post(
      `${config.apiUrl}/room-displays/${screenId}/posters/video/presign`,
      input,
    ),

  initVideoMultipart: (
    screenId: string,
    input: RoomDisplayVideoUploadInput,
  ): Promise<{ uploadId: string; key: string }> =>
    fetcher.post(
      `${config.apiUrl}/room-displays/${screenId}/posters/video-multipart/init`,
      input,
    ),

  presignVideoMultipartParts: (
    screenId: string,
    input: {
      key: string;
      uploadId: string;
      partNumbers: number[];
      expiresIn?: number;
    },
  ): Promise<{ parts: { partNumber: number; uploadUrl: string }[] }> =>
    fetcher.post(
      `${config.apiUrl}/room-displays/${screenId}/posters/video-multipart/presign-parts`,
      input,
    ),

  completeVideoMultipart: (
    screenId: string,
    input: {
      key: string;
      uploadId: string;
      parts: { partNumber: number; etag: string }[];
    },
  ): Promise<{ key: string }> =>
    fetcher.post(
      `${config.apiUrl}/room-displays/${screenId}/posters/video-multipart/complete`,
      input,
    ),

  abortVideoMultipart: (
    screenId: string,
    input: { key: string; uploadId: string },
  ): Promise<{ ok: true }> =>
    fetcher.post(
      `${config.apiUrl}/room-displays/${screenId}/posters/video-multipart/abort`,
      input,
    ),

  createPoster: (
    screenId: string,
    input: CreateRoomDisplayPosterDTO,
  ): Promise<RoomDisplayPosterDTO> =>
    fetcher.post(`${config.apiUrl}/room-displays/${screenId}/posters`, input),

  updatePoster: (
    screenId: string,
    posterId: string,
    input: UpdateRoomDisplayPosterDTO,
  ): Promise<RoomDisplayPosterDTO> =>
    fetcher.put(
      `${config.apiUrl}/room-displays/${screenId}/posters/${posterId}`,
      input,
    ),

  deletePoster: (
    screenId: string,
    posterId: string,
  ): Promise<{ ok: boolean }> =>
    fetcher.delete(
      `${config.apiUrl}/room-displays/${screenId}/posters/${posterId}`,
    ),

  reorderPosters: (
    screenId: string,
    posterIds: string[],
  ): Promise<{ data: RoomDisplayPosterDTO[] }> =>
    fetcher.put(`${config.apiUrl}/room-displays/${screenId}/posters/reorder`, {
      posterIds,
    }),
};

export const publicRoomDisplaysApi = {
  getBySlug: (slug: string): Promise<PublicDisplayPayload> =>
    fetcher.get(`${config.apiUrl}/public/displays/${encodeURIComponent(slug)}`),
};
